import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Product } from './types/product';
import { UserRole, ROLE_METADATA } from '@/components/authnz/types/rbac';
import logger from '@/lib/logging';
import { updateProductApi } from '@/lib/subscriptions-admin-api';

// Name, description and prices are mirrored from Stripe; only the granted role is app-owned.
const formSchema = z.object({
  userRole: z.nativeEnum(UserRole),
});

type FormValues = z.infer<typeof formSchema>;

interface ProductFormProps {
  product: Product;
  onSuccess: () => void;
  onCancel: () => void;
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Label className="text-sm font-medium text-muted-foreground">{label}</Label>
      <p className="text-sm mt-1 break-words">{value || '—'}</p>
    </div>
  );
}

export default function ProductForm({ product, onSuccess, onCancel }: ProductFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      userRole: product.userRole || UserRole.STUDENT,
    },
  });

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      await updateProductApi(product.id, { userRole: values.userRole });

      toast({
        title: 'Role Updated',
        description: `${product.name} now grants ${ROLE_METADATA[values.userRole].displayName}`,
      });

      onSuccess();
    } catch (error: any) {
      logger.error('Error updating product role:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update product role',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4 p-4 bg-muted rounded-lg">
          <ReadOnlyField label="Product Name" value={product.name} />
          <ReadOnlyField label="Description" value={product.description} />
          <ReadOnlyField label="Stripe Product ID" value={product.providerProductId} />
          <p className="text-xs text-muted-foreground">
            Name, description and prices come from Stripe. Change them in the Stripe Dashboard, then run
            Reconcile from Stripe.
          </p>
        </div>

        <FormField
          control={form.control}
          name="userRole"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role to Grant</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(UserRole)
                    .filter((role) => role !== UserRole.SALES && role !== UserRole.PRACTITIONER_ADMINISTRATOR)
                    .map((role) => (
                    <SelectItem key={role} value={role}>
                      {ROLE_METADATA[role].displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>Granted on purchase of any price of this product.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save Role'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
